import { action } from '@ember/object';
import { later } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import { Theme } from 'webapp/enums/enums';
import { sanitize } from 'webapp/utils/html';

import type Report from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import { themeClassNames } from 'webapp/models-custom/theme';

type ChangeSlideIndexFn = (slideIndex: number) => void;
type OnExitFn = () => void;

// this model is alive during report presentation lifecycle
// (while PreviewUrlManager isPresentView === true)
// either in browser fullscreen or not (changeable in runtime too)
// has option to open notes (opens immediately if opening not-fullscreen)
export default class ReportPresentManager {
	private report: Report;
	private theme: string;
	private changeSlideIndex: ChangeSlideIndexFn;
	private onExit: OnExitFn;

	@tracked private _isFullscreen = false;

	@tracked _currentSlideIndex = 0; // set from outside when slide changes

	@tracked windowProxy: Window | null = null;

	constructor(
		report: Report,
		changeSlideIndex: ChangeSlideIndexFn,
		onExit: OnExitFn,
		isFullscreen = false,
		theme: string,
	) {
		this.report = report;
		this.changeSlideIndex = changeSlideIndex;
		this.onExit = onExit;
		this.theme = theme || Theme.LIGHT;

		this.isFullscreen = isFullscreen;

		if (!isFullscreen) {
			this.openPresentWindow();
		}
	}

	get currentSlideIndex() {
		return this._currentSlideIndex;
	}

	set currentSlideIndex(index: number) {
		this._currentSlideIndex = index;
		this.updatePresentWindowHtml();
	}

	get isFullscreen() {
		return this._isFullscreen;
	}

	set isFullscreen(isFullscreen: boolean) {
		this._isFullscreen = isFullscreen;

		this.unbindEvents();
		this.unbindFullscreenEvents();
		if (isFullscreen) {
			document.body
				.requestFullscreen()
				.then(() => {
					this.bindFullscreenEvents();
				})
				.catch(() => {
					// skip
				});
		} else {
			if (document.fullscreenElement) {
				void document.exitFullscreen();
			}
			this.bindEvents();
		}
	}

	get currentSlide() {
		return this.report.sortedSlides[this.currentSlideIndex];
	}

	get previousSlide(): ReportSlide | null {
		return this.report.sortedSlides[this.currentSlideIndex - 1] || null;
	}

	get nextSlide(): ReportSlide | null {
		return this.report.sortedSlides[this.currentSlideIndex + 1] || null;
	}

	get totalSlides() {
		return this.report.sortedSlides.length;
	}

	get sortedSlides() {
		return this.report.sortedSlides;
	}

	@action
	private keyDownHandler(e: KeyboardEvent) {
		const isExit = e.key === 'Escape',
			isBack = ['ArrowLeft', 'p', 'P'].includes(e.key),
			isForward = ['ArrowRight', 'n', 'N'].includes(e.key);
		if (isBack || isForward) {
			this.handleNavigation(isBack);
		} else if (isExit) {
			if (!this.isFullscreen) {
				// if this is fullscreen, Esc will exit it
				// and trigger fullscreenchange event which already handles exit
				this.handleExit();
			}
		}
	}

	@action
	private fullscreenChangeHandler() {
		const enteredFullscreen = !!document.fullscreenElement;
		if (enteredFullscreen) {
			// bind navigation events
			this.bindEvents();
		} else {
			if (this.recentlyBecomeVisible || this.recentlyClickedExternalLink) {
				// stay on present mode
				this.isFullscreen = false;
			} else {
				this.handleExit();
			}
		}
	}

	handleNavigation(isBack = false) {
		const currentIndex = this.currentSlideIndex;
		const newIndex = currentIndex + (isBack ? -1 : 1);

		this.changeSlideIndexIfExists(newIndex);
	}

	handleExit() {
		this.unbindEvents();
		this.unbindFullscreenEvents();
		if (document.fullscreenElement) {
			void document.exitFullscreen();
		}
		this.onExit();
		this.closePresentWindow();
	}

	changeSlideIndexIfExists(index: number) {
		const currentSlide = this.report.sortedSlides[index];
		if (currentSlide) {
			this.changeSlideIndex(index);
		}
	}

	private bindEvents() {
		window.addEventListener('keydown', this.keyDownHandler);
	}

	private unbindEvents() {
		window.removeEventListener('keydown', this.keyDownHandler);
	}

	private bindFullscreenEvents() {
		document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
		document.addEventListener('visibilitychange', this.visibilityChangeHandler);
		window.addEventListener('click', this.clickHandler);
	}

	private unbindFullscreenEvents() {
		document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
		document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
		window.removeEventListener('click', this.clickHandler);
	}

	// ----- PRESENT popup window -----

	get isPresentWindowOpen() {
		return !!this.windowProxy;
	}

	openPresentWindow() {
		const windowFeatures = 'popup=true,left=50,top=100,width=800,height=600';
		const windowProxy = window.open('', '', windowFeatures);
		if (windowProxy) {
			this.windowProxy = windowProxy;
			this.createPresentWindowHtml();
			this.updatePresentWindowHtml();
			windowProxy.document.title = `${this.report.title} - notes`;

			windowProxy.addEventListener('beforeunload', () => {
				this.windowProxy = null;
			});
		}
	}

	private closePresentWindow() {
		if (this.windowProxy) {
			this.windowProxy.close();
			this.windowProxy = null;
		}
	}

	private createPresentWindowHtml() {
		const windowProxy = this.windowProxy;

		if (!windowProxy) {
			return;
		}

		const windowDocument = windowProxy.document;

		const selectOptions = this.report.sortedSlides
			.map((_s, index) => {
				return `<option value="${index}">Slide ${index + 1}</option>`;
			})
			.join('');

		const body = `
			<main id="wrapper">
				<section class="left">
					<select id="slide">
						${selectOptions}
					</select>
					<div class="preview current">
						<img src="" alt="" />
					</div>
					<nav class="previews">
						<a class="preview previous">
							<img src="" alt="" />
							<span>Previous</span>
						</a>
						<a class="preview next">
							<img src="" alt="" />
							<span>Next</span>
						</a>
					</nav>
				</section>
				<section class="right">
					<div id="note"></div>
				</section>
			</main>
		`;

		const themeClass = themeClassNames(this.theme);

		const html = `
			<html>
				<head>
					<link rel="stylesheet" href="/other/report-present.css" type="text/css" />
				</head>
				<body class="${themeClass}">
					${body}
				</body>
			</html>
		`;

		windowDocument.write(html);

		// events that we bind only once
		// navigation prev/next
		windowDocument.body.querySelectorAll<HTMLAnchorElement>('a.preview').forEach((node) => {
			node.addEventListener('click', (e) => {
				this.handleNavigation(node.classList.contains('previous'));
				e.preventDefault();
			});
		});

		// slide selector dropdown
		const slideSelector = windowDocument.getElementById('slide') as HTMLSelectElement;
		slideSelector.addEventListener('change', () => {
			const newIndex = +slideSelector.value;
			this.changeSlideIndexIfExists(newIndex);
		});

		windowProxy.addEventListener('keydown', this.keyDownHandler);
	}

	private updatePresentWindowHtml() {
		if (!this.windowProxy) {
			return;
		}

		const windowDocument = this.windowProxy.document;

		const slideSelector = windowDocument.getElementById('slide') as HTMLSelectElement;
		slideSelector.value = String(this.currentSlideIndex);

		windowDocument.body.querySelectorAll<HTMLElement>('.preview').forEach((node) => {
			let refSlide: ReportSlide | null = null;
			if (node.classList.contains('current')) {
				refSlide = this.currentSlide!;
			} else if (node.classList.contains('previous')) {
				refSlide = this.previousSlide;
			} else if (node.classList.contains('next')) {
				refSlide = this.nextSlide;
			}
			node.classList.toggle('empty', !refSlide);
			node.querySelector('img')!.src =
				refSlide?.shownImage || '/images/reports/placeholder-slide.svg';
		});

		const noteDiv = windowDocument.getElementById('note') as HTMLDivElement,
			noteContent = this.currentSlide?.description.content;
		noteDiv.innerHTML = noteContent ? sanitize(noteContent) : 'No notes for this slide';
		noteDiv.classList.toggle('empty', !noteContent);
	}

	// ------------ external link click logic ------------

	// when clicking an external link while in fullscreen present
	// we do not want to exit present mode completely (since this will throw us back to prev page)
	// so we flag recentlyBecomeVisible (chrome triggers exitfullscreen when original tab gets focus back)
	// or recentlyClickedExternalLink (moz & safari trigger exitfullscreen soon after clicking link)
	// this is a HACK and not really reliable.
	private recentlyBecomeVisible = false;
	private recentlyClickedExternalLink = false;

	@action
	private visibilityChangeHandler() {
		if (document.visibilityState === 'hidden') {
			return;
		}
		this.recentlyBecomeVisible = true;
		later(() => {
			this.recentlyBecomeVisible = false;
		}, 100);
	}

	@action
	private clickHandler(e: PointerEvent) {
		const target = e.target as HTMLElement;
		// only when clicked on external link
		if (target instanceof HTMLAnchorElement && target.getAttribute('target') === '_blank') {
			this.recentlyClickedExternalLink = true;
			later(() => {
				this.recentlyClickedExternalLink = false;
			}, 1000); // safari is sloooooow
		}
	}
}
